import React, { ChangeEvent, useState } from "react";
import { BoxCard } from "./BoxCard";
import { ItemCard } from "./SubProductCard";
import { InactiveSubscriptions, RechargeItemRaw } from "./types";
import { getIsoDateFormat } from "@/helpers/utils";
import { droppsProductToProductCardProduct } from "./helpers";
import DrawerHeader from "@/components/DrawerHeader";
import FullScreenDrawer from "@/components/FullScreenDrawer";
import { Button } from "@/components/Button";
import { DatePicker } from "@/components/DatePicker";
import { CircularProgress } from "@mui/material";
import { updateSubscriptionQueryData } from "./SelectedItemContext";
import { updateNextChargeDate } from "./RechargeApiActions";
import { useQueryClient } from "@tanstack/react-query";
import { generateRechargeUrl, rechargeBase } from "./RechargeApiActions";
import { NotificationProps } from "../notification/Notification";

type ErrorSubscriptionsListProps = {
  requestInProgress: boolean;
  errorSubs: InactiveSubscriptions[];
  onNextChargeDate: (notificationData: NotificationProps) => void;
};

export const ErrorSubscriptionsList = ({
  requestInProgress,
  errorSubs,
  onNextChargeDate,
}: ErrorSubscriptionsListProps) => {
  const queryClient = useQueryClient();
  const [openDrawer, setOpenDrawer] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const currDate = new Date();
  currDate.setDate(currDate.getDate() + 1); //Set to next day.
  const [newDate, setNewDate] = useState(currDate);
  const [selectedItem, setSelectedItem] = useState(errorSubs[0]);
  const paymentPageUrl = generateRechargeUrl(rechargeBase + "/payment_methods");

  const handleNextChargeDate = (item: InactiveSubscriptions) => {
    setSelectedItem(item);
    setOpenDrawer(true);
  };

  const handleDateChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newDate = new Date(event.target.value + "T00:00:00");
    if (!isNaN(+newDate)) {
      setNewDate(newDate);
    }
  };

  const handleDateSubmit = async () => {
    setLoading(true);
    let sub = null;
    try {
      sub = await updateNextChargeDate({
        orderId: selectedItem.rechargeItem.id,
        nextChargeDate: newDate,
      });
      if (sub.id) {
        updateSubscriptionQueryData(queryClient, sub);
      }
      onNextChargeDate({
        title: "Ship Date Updated",
        message: `Item scheduled for  ${
          sub.next_charge_scheduled_at
            ? new Date(sub.next_charge_scheduled_at).toLocaleDateString("en-US")
            : ""
        }`,
      });
    } catch (error) {
      onNextChargeDate({
        title: "Unable To Update Ship Date",
        severity: "error",
        message: `We have encountered some issues while updating your subscription next ship date. Please contact support.`,
      });
    }
    setOpenDrawer(false);
    setLoading(false);
  };

  if (errorSubs.length === 0) return null;

  return (
    <>
      <div className="space-y-4">
        <h2 className="title text-center text-2xl md:text-left">
          Please Update Your Payment Information
        </h2>
        <p className="body text-sm">
          We&apos;re unable to process the following order(s). The payment
          method that was attempted is no longer valid. Please update your
          payment information{" "}
          <a href={paymentPageUrl} aria-label="Link to open billing page">
            here
          </a>
          , then select your next charge date for your orders.
        </p>
        <ol className="space-y-8">
          {errorSubs.map((item) => (
            <ErrorSubscription
              key={"error sub" + item.rechargeItem.id}
              item={item}
              requestInProgress={requestInProgress}
              onNextChargeDate={() => {
                handleNextChargeDate(item);
              }}
            />
          ))}
        </ol>
      </div>
      <FullScreenDrawer
        anchor="right"
        open={openDrawer}
        onClose={() => setOpenDrawer(false)}
        fullscreen={undefined}
        role="dialog"
        aria-label="Drawer to select next charge date for subscription"
        aria-modal={true}
        BackdropProps={{ className: "bg-transparent" }}
      >
        <DrawerHeader
          sticky={true}
          handleClose={() => setOpenDrawer(false)}
          text={"Next Charge Date"}
        />
        <div className="w-full space-y-4 p-5">
          <DatePicker
            name="next-shipment-date"
            label="Change Ship Date"
            value={getIsoDateFormat(newDate)}
            onChange={handleDateChange}
            min={getIsoDateFormat(new Date())}
            className="appearance-none"
          />
          <Button
            className="w-full"
            disabled={currDate === newDate}
            onClick={handleDateSubmit}
            loading={isLoading}
          >
            Change Date
          </Button>
        </div>
        {isLoading && (
          <>
            <div className="absolute h-full w-full cursor-wait bg-white opacity-50 transition"></div>
            <div className="absolute flex h-full w-full cursor-wait items-center justify-center">
              <CircularProgress />
            </div>
          </>
        )}
      </FullScreenDrawer>
    </>
  );
};

const ErrorSubscription = ({
  item,
  onNextChargeDate,
  requestInProgress,
}: {
  item: InactiveSubscriptions;
  onNextChargeDate: () => void;
  requestInProgress: boolean;
}) => {
  const { rechargeItem: subscription, address, product, variant } = item;
  if (!subscription.cadence) {
    return null;
  }

  const subscriptionActions = [
    {
      displayText: "Select Next Charge Date",
      action: onNextChargeDate,
    },
  ];

  const productCardProduct = droppsProductToProductCardProduct({
    product,
    variant,
  });

  return (
    <li>
      <BoxCard address={address} data-address-id={address.id} status="Error">
        <ItemCard
          responsive={true}
          product={productCardProduct}
          price={subscription.price}
          shippingInfo={subscription.cadence}
          subscriptionActions={subscriptionActions}
          quantity={subscription.quantity}
          disabled={requestInProgress}
          data-subscription-id={subscription.id}
        />
      </BoxCard>
    </li>
  );
};
